import { GripVertical, Loader2 } from 'lucide-react'
import * as React from 'react'
import SortableList, { SortableItem } from 'react-easy-sort'

import { useReorderChannels } from '@/hooks/use-reorder-channels'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { Channel } from '@/types/group-channel.type'

interface ReorderChannelsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  channels: Channel[]
  groupName?: string
}

export function ReorderChannelsDialog({ open, onOpenChange, channels, groupName }: ReorderChannelsDialogProps) {
  const [localChannels, setLocalChannels] = React.useState<Channel[]>([])
  const { reorderChannels, isPending } = useReorderChannels()

  React.useEffect(() => {
    setLocalChannels(channels)
  }, [channels, open])

  const handleSortEnd = (oldIndex: number, newIndex: number) => {
    setLocalChannels(prev => {
      const next = [...prev]
      const [moved] = next.splice(oldIndex, 1)
      next.splice(newIndex, 0, moved)
      return next
    })
  }

  const handleSave = async () => {
    try {
      await reorderChannels(localChannels)
      onOpenChange(false)
    } catch {
      // error shown via toast or can be extended here
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Sắp xếp kênh{groupName ? ` — ${groupName}` : ''}</DialogTitle>
        </DialogHeader>

        <SortableList
          onSortEnd={handleSortEnd}
          className="relative z-50 flex flex-col gap-1 select-none py-1"
          draggedItemClassName="!z-[99999] shadow-xl opacity-80"
        >
          {localChannels.map(channel => (
            <SortableItem key={channel.id}>
              <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm cursor-grab active:cursor-grabbing hover:bg-accent transition-colors z-50">
                <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="truncate">{channel.name}</span>
              </div>
            </SortableItem>
          ))}
        </SortableList>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Hủy
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Đang lưu…
              </>
            ) : (
              'Lưu thứ tự'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
